import React, { useState, useEffect } from 'react';
import { Globe, Compass } from 'lucide-react';
import { MapContainer, TileLayer, GeoJSON, useMap, Popup } from 'react-leaflet';

function Map() {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [animateMap, setAnimateMap] = useState(false);
  const [geoJsonData, setGeoJsonData] = useState(null);

  useEffect(() => {
    setAnimateMap(true);
    fetch('https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson')
      .then(response => response.json())
      .then(data => setGeoJsonData(data))
      .catch(error => console.error('Error loading GeoJSON:', error));
  }, []);

  const MapEvents = () => {
    const map = useMap();
    useEffect(() => {
      map.setView([20, 0], 2);
    }, [map]);
    return null;
  };

  const onEachFeature = (feature, layer) => {
    const countryName = feature.properties.ADMIN;
    layer.bindPopup(countryName);
    layer.on({
      click: () => {
        setSelectedCountry(countryName);
        fetchEvents('country', countryName);
      },
      mouseover: (e) => {
        e.target.setStyle({ fillColor: '#8b4513', fillOpacity: 0.7 });
      },
      mouseout: (e) => {
        e.target.setStyle({ fillColor: '#d4a373', fillOpacity: 0.5 });
      },
    });
  };

  const fetchEvents = async (type, name = null) => {
    const query = name || (type === 'country' ? selectedCountry : selectedState);
    if (!query) return;

    setLoading(true);
    try {
      const res = await fetch(`https://aitutorbackend-production.up.railway.app/history/${type}?name=${encodeURIComponent(query)}`);
      // const res = await fetch(`http://localhost:5000/history/${type}?name=${encodeURIComponent(query)}`);
      const data = await res.json();
      setEvents(data.events || [`No historical events found for this ${type}.`]);
    } catch (error) {
      console.error(`Error fetching ${type} events:`, error);
      setEvents([`Failed to fetch ${type} events. Please try again.`]);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-parchment pt-16 font-serif">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Globe className="h-16 w-16 text-parchment-dark" />
          </div>
          <h1 className="vintage-title">Historical Atlas</h1>
          <p className="vintage-subtitle max-w-3xl mx-auto">Explore the rich history of countries and states across the world</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className={`vintage-card transition-all duration-1000 ${animateMap ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <MapContainer center={[20, 0]} zoom={2} scrollWheelZoom={true}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <MapEvents />
              {geoJsonData && (
                <GeoJSON
                  data={geoJsonData}
                  style={() => ({ fillColor: '#d4a373', weight: 1, opacity: 1, color: '#937153', fillOpacity: 0.5 })}
                  onEachFeature={onEachFeature}
                />
              )}
            </MapContainer>
          </div>

          <div className="space-y-6">
            <div className="vintage-card">
              <div className="flex items-center gap-4 mb-4">
                <Globe className="h-6 w-6 text-parchment-dark" />
                <h3 className="text-xl font-bold text-parchment-dark">Search by Country</h3>
              </div>
              <div className="flex gap-2">
                <input type="text" className="vintage-input" placeholder="Enter country name..." value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)} />
                <button onClick={() => fetchEvents('country')} className="vintage-button">Search</button>
              </div>
            </div>

            <div className="vintage-card">
              <div className="flex items-center gap-4 mb-4">
                <Compass className="h-6 w-6 text-parchment-dark" />
                <h3 className="text-xl font-bold text-parchment-dark">Search by State, City & Area</h3>
              </div>
              <div className="flex gap-2">
                <input type="text" className="vintage-input" placeholder="Enter State, City & Area..." value={selectedState} onChange={(e) => setSelectedState(e.target.value)} />
                <button onClick={() => fetchEvents('state')} className="vintage-button">Search</button>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-parchment-dark border-t-transparent"></div>
              </div>
            ) : events.length > 0 && (
              <div className="vintage-card">
                <h3 className="text-xl font-bold text-parchment-dark mb-4">Historical Events</h3>
                <ul className="space-y-4">
                  {events.map((event, index) => (
                    <li key={index} className="text-[#5c2e0a] animate-fadeIn" style={{ animationDelay: `${index * 0.2}s` }}>{event}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Map;